<!-- CoachApplication.vue -->
<script setup>
import { ref, onMounted } from "vue";
import * as Yup from "yup";
import { CoachApplicationFormEmail } from "../../mixins/api";
import { useToast } from "vue-toastification";
import Modal from "./Modal.vue";
import CloseIcon from "../../assets/svg/CloseIcon.vue";
import Button from "./Button.vue";

const toast = useToast();
const props = defineProps({
  element: {
    type: Object,
  },
})
const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().required("Email is required").email("Email is invalid"),
  phone: Yup.string().required("Phone number is required"),
  teachILKBClassesCity: Yup.string().required("City name is required"),
  teachILKBClassesState: Yup.string().required("State name is required"),
  zipcode: Yup.string().required("Zipcode is required").matches(/^\d{5}(-\d{4})?$/, "Please enter a valid zipcode"),
});

const CoachApplicationDefault = {
  name: "",
  email: "",
  phone: "",
  address: "",
  zipcode: "",
  fitnessCertifications: false,
  boxing_MartialArtsExperience: false,
  teachILKBClassesCity: "",
  teachILKBClassesState: "",
  submitted: false,
};

const CoachApplication = ref({ ...CoachApplicationDefault });

const CoachAppSuccess = ref("");
const CoachAppError = ref("");
const CoachAppFormSubmitting = ref(false);

// Modal state management
const isModalVisible = ref(false);
const showModal = () => {
  // Reset form when opening modal
  CoachApplication.value = { ...CoachApplicationDefault };
  isModalVisible.value = true;
};
const closeModal = () => {
  isModalVisible.value = false;
};

const onCoachAppSubmit = async () => {
  try {
    CoachAppFormSubmitting.value = true;
    console.log("Submitting:", CoachApplication.value);

    const response = await CoachApplicationFormEmail(CoachApplication.value);

    if (response) {
      toast.success("Thank you. We'll be in contact with you shortly.");
      CoachApplication.value = { ...CoachApplicationDefault, submitted: true };
      closeModal(); // Close modal on success
    } else {
      toast.error("Please check and try again.");
    }
  } catch (err) {
    toast.error("Something went wrong!");
  } finally {
    CoachAppFormSubmitting.value = false;
  }
};

onMounted(() => {
  console.log("LeadCaptureModal mounted");
});
</script>
<template>
  <div  :class="{
      'coach-application-container': element.module_section_element_name !== 'COACH_BEGIN_APPLICATION_BUTTON'
    }">
    <!-- Apply Now Button -->
    <Button  v-if="element.element_type === 'CoachApplication'"
      @click="showModal"
      text="APPLY TODAY"
      styled="primary"
      class="apply-now-btn"
    />
     <Button  v-else-if="element.element_type === 'HOW_TO_BECOME_COACH'"
      @click="showModal"
      text="APPLY TODAY"
      styled="primary"
      class="apply-now-btn"
    />
      <Button  v-else-if="element.module_section_element_name === 'BUTTON_COACH_ENROLL_NOW'"
                        class="btn btn-primary"
                          @click="showModal"
                           styled="primary"
                        text="APPLY TODAY"
                    />  
               <Button  v-else-if="element.module_section_element_name === 'COACH_BEGIN_APPLICATION_BUTTON'"
                        class="btn btn-danger"
                        style="float: left;"
                         styled="primary"
                          @click="showModal"
                          text="APPLY TODAY"
                    /> 
    <!-- Modal with Form -->
    <Teleport to="body">
      <Modal modalLarge="true" v-if="isModalVisible">
        <template #body>
        <span class="modal-close" @click="closeModal">
          <CloseIcon />
        </span>
        <div class="lead-form">
          <div class="form-header coach-header-center">
            <h2 class="coach-title-center">Coach Application</h2>
          </div>
          
          <form @submit.prevent="onCoachAppSubmit">
            <!-- Contact Info -->
            <div class="form-row">
              <div class="form-group">
                <label>Full Name *</label>
                <input 
                  class="form-control" 
                  type="text" 
                  v-model="CoachApplication.name" 
                  placeholder="Enter your full name"
                />
              </div>
              <div class="form-group">
                <label>Email Address *</label>
                <input 
                  type="email" 
                  class="form-control" 
                  v-model="CoachApplication.email" 
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Phone Number *</label>
                <input 
                  type="tel" 
                  class="form-control" 
                  v-model="CoachApplication.phone" 
                  placeholder="(555) 123-4567"
                />
              </div>
              <div class="form-group">
                <label>City *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="CoachApplication.teachILKBClassesCity" 
                  placeholder="Your city"
                />
              </div>
              <div class="form-group">
                <label>State *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="CoachApplication.teachILKBClassesState" 
                  placeholder="Your state"
                />
              </div>
            </div>
            <div class="form-row address-row">
              <div class="form-group address-field">
                <label>Address *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="CoachApplication.address" 
                  placeholder="Address"
                />
              </div>
              <div class="form-group zipcode-field">
                <label>Zipcode *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="CoachApplication.zipcode" 
                  placeholder="Zipcode"
                  maxlength="10"
                />
              </div>
            </div>
            <!-- Experience -->
            <div class="form-switches">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="fitnessCertifications"
                  v-model="CoachApplication.fitnessCertifications"
                />
                <label class="form-check-label" for="fitnessCertifications">
                  <span class="check-icon">{{ CoachApplication.fitnessCertifications ? '✓' : '' }}</span>
                  Yes, I have Fitness Certifications (not required)
                </label>
              </div>

              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="boxingExp"
                  v-model="CoachApplication.boxing_MartialArtsExperience"
                />
                <label class="form-check-label" for="boxingExp">
                  <span class="check-icon">{{ CoachApplication.boxing_MartialArtsExperience ? '✓' : '' }}</span>
                  Yes, I have Kickboxing/Boxing experience (not required)
                </label>
              </div>
            </div>

            <!-- Submit -->
            <div class="form-actions">
              <button
                type="submit"
                :disabled="CoachAppFormSubmitting"
                :class="{
                  'btn btn-danger submitted': CoachApplication.submitted,
                  'btn btn-primary submit-btn': !CoachApplication.submitted,
                }"
              >
                <span v-if="CoachAppFormSubmitting" class="loading-spinner"></span>
                {{ CoachApplication.submitted ? "✓ Application Submitted" : "Submit Application" }}
              </button>
            </div>
          </form>

          <!-- Disclaimer -->
          <p class="disclaimer">
            By submitting this form, I agree to receive emails, text messages, and
            other communications, including informational and promotional messages,
            from I Love Kickboxing. Message frequency varies. I can opt out at any
            time by replying STOP or get help by replying HELP. Standard message rates
            may apply. For further information please see our
            <a href="https://www.ilovekickboxing.com/privacy-policy" target="_blank">Privacy Policy</a>
            and our
            <a href="https://www.ilovekickboxing.com/terms-of-use" target="_blank">Terms of Service</a>.
          </p>
        </div>
      </template>
    </Modal>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.coach-application-container {
  text-align: center;
  
  .apply-now-btn {
    font-size: 18px;
    font-weight: 600;
    padding: 12px 32px;
    color:black;
  }
}

// Ensure all modal content is properly centered regardless of button context
div {
  .lead-form {
    .form-header {
      text-align: center !important;
      width: 100% !important;
      display: block !important;
      
      h2 {
        text-align: center !important;
        display: block !important;
        width: 100% !important;
        margin: 0 auto !important;
      }
    }
  }
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;
  border: 2px solid #e5e7eb;
  
  &:hover {
    background: white;
    border-color: #c5a254;
    transform: scale(1.1);
  }
  
  svg {
    width: 18px;
    height: 18px;
    color: #374151;
  }
}

.lead-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  position: relative;
  font-size: 14px; // Reset font size to default


  .form-header {
    text-align: center !important;
    margin-bottom: 20px;
    padding: 8px 0 16px 0;
    border-bottom: 2px solid #f0f0f0;
    width: 100% !important;
    display: block !important;

    h2 {
      color: #2c3e50;
      font-size: 28px !important;
      font-weight: 700 !important;
      margin: 0 auto !important;
      line-height: 1.3 !important;
      padding: 4px 0;
      background: linear-gradient(135deg, #c5a254 0%, #d4af37 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      text-transform: none !important;
      letter-spacing: normal !important;
      font-family: 'pp_formula_condensedbold' !important;
      text-align: center !important;
      display: block !important;
      width: 100% !important;
    }
  }

  .form-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }

  .address-row {
    grid-template-columns: 2fr 1fr;
  }

  .form-group {
    display: flex;
    flex-direction: column;

    label {
      font-weight: 600;
      margin-bottom: 6px;
      font-size: 14px;
      color: #374151;
    }

    .form-control {
      padding: 12px 14px;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      font-size: 14px;
      transition: all 0.3s ease;
      background: white;
      color: #374151;

      &::placeholder {
        color: #9ca3af;
        font-style: italic;
      }

      &:focus {
        border-color: #c5a254;
        outline: none;
        box-shadow: 0 0 0 3px rgba(197, 162, 84, 0.1);
      }

      &:hover {
        border-color: #d1d5db;
      }
    }
  }

  .form-switches {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 16px;

    .form-check {
      position: relative;
      padding: 12px 16px;
      background: white;
      border: 2px solid #e9ecef;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        border-color: #c5a254;
      }

      input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        width: 100%;
        height: 100%;
        margin: 0;
        top: 0;
        left: 0;

        &:checked + label {
          .check-icon {
            transform: scale(1);
          }
        }
      }

      label {
        margin: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        font-size: 14px;
        color: #374151;
        transition: all 0.3s ease;

        .check-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid #d1d5db;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;
          font-size: 12px;
          font-weight: bold;
          background: white;
          transition: all 0.3s ease;
          transform: scale(0.8);
        }
      }
    }
  }

  .form-actions {
    text-align: center;
    margin: 16px 0 8px 0;

    .submit-btn, .submitted {
      padding: 14px 32px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 50px;
      border: none;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      min-width: 180px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      &:not(:disabled):hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      }

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
    }

    .submit-btn {
      background: linear-gradient(135deg, #c5a254 0%, #d4af37 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(197, 162, 84, 0.4);
    }

    .submitted {
      background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
      color: white;
    }

    .loading-spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      border-top-color: white;
      animation: spin 1s ease-in-out infinite;
    }
  }

  .disclaimer {
    font-size: 11px;
    line-height: 1.4;
    text-align: center;
    color: #6c757d;
    background: #f8f9fa;
    padding: 8px;
    border-radius: 6px;
    border-left: 3px solid #c5a254;
    margin: 0;

    a {
      color: #c5a254;
      font-weight: 600;
      text-decoration: none;
      transition: color 0.3s ease;

      &:hover {
        color: #d4af37;
        text-decoration: underline;
      }
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Force centering with specific classes
.coach-header-center {
  text-align: center !important;
  width: 100% !important;
  display: block !important;
  margin: 0 auto !important;
}

.coach-title-center {
  text-align: center !important;
  width: 100% !important;
  display: block !important;
  margin: 0 auto !important;
  text-align: center !important;
}

// Global override for consistent header sizing across all button contexts
:deep(.modal-body) {
  font-size: 14px;
  
  .lead-form {
    font-size: 14px;
    
    .form-header {
      text-align: center !important;
      
      h2 {
        font-size: 28px !important;
        font-weight: 700 !important;
        line-height: 1.3 !important;
        margin: 0 auto !important;
        text-transform: none !important;
        font-family: 'pp_formula_condensedbold' !important;
        text-align: center !important;
        display: block !important;
        width: 100% !important;
      }
    }
  }
}

// Mobile responsive adjustments
@media screen and (max-width: 767px) {
  .lead-form {
    padding: 0;
    margin: 0;
  }
  
  .modal-close {
    top: 12px;
    right: 16px;
    width: 28px;
    height: 28px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
